import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  setSearchFilter,
  setLocationFilter,
  setProductFilter,
  setStatusFilter,
  fetchSchemes,
} from '../../store/schemeSlice';
import { getCitiesAPI } from '../../api/locations';
import { getProducts } from '../../api/products';
import { CustomDropdown } from '../Common/CustomDropdown';

export const SchemeFilters: React.FC = () => {
  const dispatch = useAppDispatch();
  const { filters } = useAppSelector((state) => state.scheme);

  const [citiesList, setCitiesList] = useState<string[]>([]);
  const [productsList, setProductsList] = useState<string[]>([]);

  useEffect(() => {
    const loadDropdownData = async () => {
      try {
        const [citiesRes, prodRes] = await Promise.allSettled([
          getCitiesAPI(),
          getProducts({ limit: 100 }),
        ]);

        if (citiesRes.status === 'fulfilled' && citiesRes.value?.data) {
          const names = citiesRes.value.data.map((c: any) => c.name);
          setCitiesList(names);
        }
        if (prodRes.status === 'fulfilled' && prodRes.value?.data?.products) {
          const names = prodRes.value.data.products.map((p: any) => p.name);
          setProductsList(names);
        }
      } catch (e) {
        console.warn('Could not load location/product dropdowns for filter:', e);
      }
    };
    loadDropdownData();
  }, []);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setSearchFilter(e.target.value));
    dispatch(fetchSchemes());
  };

  const handleLocationChange = (val: string) => {
    dispatch(setLocationFilter(val));
    dispatch(fetchSchemes());
  };

  const handleProductChange = (val: string) => {
    dispatch(setProductFilter(val));
    dispatch(fetchSchemes());
  };

  const handleStatusChange = (val: string) => {
    dispatch(setStatusFilter(val));
    dispatch(fetchSchemes());
  };

  return (
    <div className="bg-white rounded-2xl p-4 md:p-5 mb-6 shadow-sm border border-gray-100">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
        {/* Search Input */}
        <div>
          <label className="block text-gray-700 font-semibold text-xs mb-1">
            Search
          </label>
          <div className="relative">
            <input
              type="text"
              placeholder="Search scheme name or ID..."
              value={filters.search}
              onChange={handleSearchChange}
              className="w-full border border-[#828282] rounded-2xl py-2 px-3.5 outline-none focus:border-[#B71C1C] text-xs font-semibold text-gray-800 placeholder-gray-400 bg-white transition-colors"
            />
          </div>
        </div>

        {/* Location Dropdown */}
        <CustomDropdown
          label="Location"
          placeholder="All Locations"
          options={['All Locations', ...citiesList]}
          value={filters.location || 'All Locations'}
          onChange={handleLocationChange}
        />

        {/* Product Dropdown */}
        <CustomDropdown
          label="Product"
          placeholder="All Products"
          options={['All Products', ...productsList]}
          value={filters.product === 'SKU Unit' ? 'All Products' : filters.product || 'All Products'}
          onChange={handleProductChange}
        />

        {/* Status Dropdown */}
        <CustomDropdown
          label="Status"
          placeholder="Status"
          options={['Status', 'Active', 'Expired', 'Upcoming']}
          value={filters.status === 'SKU Unit' ? 'Status' : filters.status || 'Status'}
          onChange={handleStatusChange}
          searchable={false}
        />
      </div>
    </div>
  );
};
